/** Shared parsing for Chat ABAP HTTP API responses */

import axios from 'axios';
import { isHiddenChatMessage } from '@/lib/chatDisplay';

export function formatApiError(err) {
  const d = err?.response?.data;
  if (d == null) return err?.message || 'Request failed';
  if (typeof d.detail === 'string') return d.detail;
  if (Array.isArray(d.detail)) {
    return d.detail.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join('; ');
  }
  if (typeof d === 'object') return JSON.stringify(d);
  return String(d);
}

export function normalizeSessionsPayload(data) {
  if (Array.isArray(data)) {
    return { items: data, total: null };
  }
  if (data && typeof data === 'object') {
    const raw =
      data.sessions ??
      data.items ??
      data.data ??
      data.results ??
      data.records ??
      [];
    const items = Array.isArray(raw) ? raw : [];
    const total =
      typeof data.total === 'number'
        ? data.total
        : typeof data.count === 'number'
          ? data.count
          : typeof data.total_count === 'number'
            ? data.total_count
            : null;
    return { items, total };
  }
  return { items: [], total: null };
}

export function sessionIdOf(row) {
  if (!row || typeof row !== 'object') return '';
  return (
    row.session_id ??
    row.sessionId ??
    row.id ??
    row._id ??
    row.uuid ??
    ''
  );
}

export function phaseOf(row) {
  const p = row?.phase ?? row?.status ?? row?.state ?? '';
  return typeof p === 'string' ? p : String(p || '');
}

export function previewOf(row) {
  const v =
    row?.last_message ??
    row?.lastMessage ??
    row?.preview ??
    row?.summary ??
    row?.title ??
    row?.message ??
    '';
  if (typeof v !== 'string') {
    try {
      return JSON.stringify(v).slice(0, 200);
    } catch {
      return '';
    }
  }
  return v;
}

export function dateOf(row) {
  const d =
    row?.updated_at ??
    row?.updatedAt ??
    row?.modified_at ??
    row?.last_activity_at ??
    row?.created_at ??
    row?.createdAt ??
    null;
  if (!d) return null;
  const t = Date.parse(d);
  return Number.isNaN(t) ? null : new Date(t);
}

function normalizeOneMessage(m) {
  if (m == null) return { role: 'assistant', content: '' };
  if (typeof m === 'string') return { role: 'assistant', content: m };
  if (typeof m !== 'object') return { role: 'assistant', content: String(m) };
  const roleRaw = m.role ?? m.sender ?? m.type ?? m.author ?? 'assistant';
  const r = String(roleRaw).toLowerCase();
  const isUser = r === 'user' || r === 'human' || r === 'customer';
  const content = m.content ?? m.text ?? m.body ?? m.message ?? '';
  const text = typeof content === 'string' ? content : JSON.stringify(content ?? '');
  const generatedCode = m.generated_code ?? m.generatedCode ?? null;
  const phase = m.phase ?? null;
  const awaitingUser = m.awaiting_user ?? m.awaitingUser;
  return {
    role: isUser ? 'user' : 'assistant',
    content: text,
    generatedCode: generatedCode || null,
    phase: phase ? String(phase) : null,
    awaiting_user: awaitingUser === undefined || awaitingUser === null ? null : Boolean(awaitingUser),
  };
}

/** Strip ```abap fences from stored generated_code. */
export function stripCodeFences(code) {
  if (code == null) return null;
  const trimmed = String(code).trim();
  if (!trimmed) return null;
  const fenced = trimmed.match(/^```(?:abap)?\s*([\s\S]*?)```$/i);
  return fenced ? fenced[1].trim() : trimmed;
}

export function deriveThreadMetaFromMessages(messages) {
  let phase = '';
  let awaitingUser = null;
  let generatedCode = null;

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!phase && m.phase) {
      phase = String(m.phase).toUpperCase();
    }
    if (!generatedCode && m.generatedCode && String(m.generatedCode).trim()) {
      generatedCode = stripCodeFences(m.generatedCode);
    }
    if (m.role === 'assistant' && awaitingUser === null && m.awaiting_user != null) {
      awaitingUser = Boolean(m.awaiting_user);
    }
  }

  if (generatedCode && !phase) {
    phase = 'DONE';
  }
  if (phase === 'DONE') {
    awaitingUser = false;
  }

  return {
    phase,
    awaiting_user: awaitingUser,
    generated_code: generatedCode,
  };
}

function mergeThreadMeta(...sources) {
  const out = { phase: '', awaiting_user: null, generated_code: null, prd_available: false };
  for (const src of sources) {
    if (!src) continue;
    if (src.phase) out.phase = String(src.phase).toUpperCase();
    const au = src.awaiting_user ?? src.awaitingUser;
    if (au !== undefined && au !== null) out.awaiting_user = Boolean(au);
    const gc = src.generated_code ?? src.generatedCode;
    if (gc != null && String(gc).trim() !== '') {
      out.generated_code = stripCodeFences(gc);
    }
    if (src.prd_available ?? src.prdAvailable) {
      out.prd_available = true;
    }
  }
  return out;
}

export function normalizeMessagesPayload(data) {
  let raw = [];
  let meta = {};

  if (Array.isArray(data)) {
    raw = data;
  } else if (data && typeof data === 'object') {
    raw =
      data.messages ??
      data.items ??
      data.thread ??
      data.data ??
      data.history ??
      [];
    if (!Array.isArray(raw)) raw = [];
    meta = {
      phase: data.phase ?? data.state,
      awaiting_user: data.awaiting_user ?? data.awaitingUser,
      generated_code: data.generated_code ?? data.generatedCode,
      prd_available: data.prd_available ?? data.prdAvailable,
      session_id: data.session_id ?? data.sessionId,
    };
  }

  const messages = raw
    .map(normalizeOneMessage)
    .filter((m) => !isHiddenChatMessage(m) && (m.content !== '' || m.role === 'user'));
  const derived = deriveThreadMetaFromMessages(messages);
  meta = mergeThreadMeta(meta, derived);
  return { messages, meta };
}

export async function loadChatSessionThread(sessionId, apiBase) {
  if (!sessionId || !apiBase) {
    return { messages: [], meta: {} };
  }
  const base = String(apiBase).replace(/\/$/, '');
  const [msgRes, statusRes] = await Promise.all([
    axios.get(`${base}/chat/${sessionId}/messages`),
    axios.get(`${base}/chat/${sessionId}/status`).catch(() => null),
  ]);
  let { messages, meta: msgMeta } = normalizeMessagesPayload(msgRes.data);
  let meta = { ...msgMeta };
  if (statusRes?.data) {
    const s = mapChatResponse(statusRes.data);
    meta = mergeThreadMeta(meta, {
      phase: s.phase,
      awaiting_user: s.awaitingUser,
      generated_code: s.generatedCode,
      prd_available: s.prdAvailable,
    });
    // If the transcript store has no messages (e.g. created before databasehandler
    // was wired up), fall back to the last assistant text from the status endpoint
    // so sidebar clicks always show at least the most recent assistant message.
    if (messages.length === 0 && s.message) {
      const fallback = { role: 'assistant', content: String(s.message).trim() };
      if (!isHiddenChatMessage(fallback) && fallback.content) {
        messages = [fallback];
      }
    }
  }
  return { messages, meta };
}

export function mapChatResponse(data) {
  return {
    sessionId: data.session_id,
    awaitingUser: Boolean(data.awaiting_user),
    phase: data.phase ?? '',
    message: data.message ?? '',
    generatedCode: data.generated_code ?? null,
    prdAvailable: Boolean(data.prd_available),
    generating: Boolean(data.generating),
  };
}

const PROCESSING_PLACEHOLDER = 'Processing your reply';

function isProcessingPlaceholder(text) {
  if (!text) return false;
  return text.replace(/[.\u2026…]/g, '').trim().toLowerCase() === PROCESSING_PLACEHOLDER.toLowerCase();
}

/** Poll GET /chat/{sessionId}/status until async graph work finishes (Halo gateway path). */
export async function pollChatUntilReady(apiBase, sessionId, { intervalMs = 2000, maxWaitMs = 180000 } = {}) {
  const base = (apiBase || '').replace(/\/$/, '');
  if (!base || !sessionId) {
    throw new Error('pollChatUntilReady requires apiBase and sessionId');
  }

  const started = Date.now();
  let last = null;

  while (Date.now() - started < maxWaitMs) {
    const { data } = await axios.get(`${base}/chat/${sessionId}/status`);
    last = mapChatResponse(data);
    if (!last.generating) {
      return last;
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  if (last) return last;
  throw new Error('Chat status polling timed out');
}

export { isProcessingPlaceholder };
